/** `read` is sent again once renewed, `write` once renewed while it waited, `false` never (`Session` of `@beyond-js/ui`) */
export type Replay = 'read' | 'write' | false;

/** What a request whose session ended waits on: the page's `Session` of `@beyond-js/ui`, once it exists */
interface Keeper {
	lost(lost: { reason?: string | null; replay?: Replay }): Promise<boolean>;
	text(key: string): string;
}

/**
 * Where a request whose session ended waits (the family rule on a session that ends while the person works,
 * Beyond Suite's `docs/family/session-renewal.md`). The API client hands every refusal of the session it sent to the
 * page's `Session`, which renews it without being seen or asks the person once, and answers whether to send
 * the request again. Until the person is known there is no `Session`, and nothing waits: the refusal is the
 * answer, and the signed-out arrival takes over.
 */
export class Lapse {
	/** The answers that say the session a request was sent with ended */
	static CODES = Object.freeze(['UNAUTHENTICATED', 'SESSION_EXPIRED', 'SESSION_REVOKED', 'ACCOUNT_SUSPENDED']);

	#keeper: Keeper | undefined;

	/**
	 * @returns The function that lets go of it again
	 */
	attach(keeper: Keeper): () => void {
		this.#keeper = keeper;
		return () => void (this.#keeper === keeper && (this.#keeper = undefined));
	}

	/** Whether a `Session` answers for the page: until then a refusal is the answer, thrown as it is */
	get present(): boolean {
		return this.#keeper !== undefined;
	}

	/**
	 * @returns Whether the caller sends its request again
	 */
	wait(reason: string | undefined, replay: Replay): Promise<boolean> {
		return this.#keeper ? this.#keeper.lost({ reason: reason ?? null, replay }) : Promise.resolve(false);
	}

	/** The line beside a destructive action that was not sent while the person was signed out */
	get unsent(): string | undefined {
		return this.#keeper?.text('unsent');
	}
}
